import { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import {
  Clock,
  TrendingDown,
  Ship,
  ShieldOff,
  FileSpreadsheet,
  type LucideIcon,
} from 'lucide-react';

// ── Data ────────────────────────────────────────────────────────

interface PainPoint {
  icon: LucideIcon;
  title: string;
  description: string;
  solutionHint: string;
  stat?: string;
}

const painPoints: PainPoint[] = [
  {
    icon: Clock,
    title: 'Cotizar toma horas, no minutos',
    description:
      'Tu equipo arma cotizaciones en Excel, copia y pega precios, recalcula márgenes a mano. Un error y pierdes la venta — o peor, vendes sin margen.',
    solutionHint: '→ Módulo de Cotizaciones',
    stat: '15h/mes por vendedor recuperadas',
  },
  {
    icon: TrendingDown,
    title: 'Tus precios no reflejan el mercado',
    description:
      'La competencia cambia precios y tú te enteras días después. Sin datos en tiempo real, compites a ciegas y pierdes deals que podías ganar.',
    solutionHint: '→ Pricing Dinámico',
    stat: '3.700+ precios scrapeados ahora',
  },
  {
    icon: Ship,
    title: 'Los costos de importación son una caja negra',
    description:
      'Fletes, aranceles, tipos de cambio — cada importación tiene costos ocultos que no llegan a tu precio de venta. Tu margen real es una incógnita.',
    solutionHint: '→ Seguimiento de Importaciones',
    stat: 'Costo desglosado por SKU',
  },
  {
    icon: ShieldOff,
    title: 'Descuentos que aprueba quien no debe',
    description:
      'Sin un workflow real, los vendedores aplican descuentos por su cuenta y nadie ve qué cotizaciones se cerraron por debajo del piso de margen — hasta que es tarde.',
    solutionHint: '→ Workflow de Aprobaciones',
    stat: '+8% margen recuperado',
  },
  {
    icon: FileSpreadsheet,
    title: 'Reportes que cada uno arma a mano',
    description:
      'Lunes a la mañana: tres planillas, cuatro versiones, nadie sabe cuál es la buena. Pierdes una hora por reporte y otra hora discutiendo de qué número estamos hablando.',
    solutionHint: '→ Analytics & Dashboards',
    stat: 'Dashboards en vivo, sin Excel',
  },
];

// ── Card ────────────────────────────────────────────────────────

function PainPointCard({ pain, index }: { pain: PainPoint; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const Icon = pain.icon;

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  const rotateY = useSpring(useTransform(rawX, [-0.5, 0.5], [-10, 10]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateX = useSpring(useTransform(rawY, [-0.5, 0.5], [8, -8]), {
    stiffness: 120,
    damping: 18,
  });
  const glareX = useTransform(rawX, [-0.5, 0.5], ['0%', '100%']);
  const glareY = useTransform(rawY, [-0.5, 0.5], ['0%', '100%']);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ perspective: '900px' }}
    >
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        className="group relative h-full rounded-xl border border-gray-100 dark:border-gray-800
          border-t-4 border-t-red-500/70 dark:border-t-orange-500/60
          bg-white dark:bg-gray-900/50 dark:backdrop-blur-sm p-6
          hover:border-red-200 dark:hover:border-orange-500/30
          shadow-sm hover:shadow-xl dark:hover:shadow-orange-500/5
          transition-shadow duration-300 cursor-default"
      >
        {/* Glare highlight */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: useTransform(
              [glareX, glareY],
              ([x, y]) =>
                `radial-gradient(circle at ${x} ${y}, rgba(255,255,255,0.45) 0%, transparent 65%)`
            ),
          }}
        />

        {/* Icon — floats in Z */}
        <div style={{ transform: 'translateZ(20px)' }}>
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mb-4 shadow-lg">
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Text — slightly elevated */}
        <div style={{ transform: 'translateZ(10px)' }}>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{pain.title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            {pain.description}
          </p>
          <p className="text-xs font-semibold text-red-600 dark:text-orange-400 tracking-wide mb-3">
            {pain.solutionHint}
          </p>
          {pain.stat && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                {pain.stat}
              </span>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Section ─────────────────────────────────────────────────────

export function PainPointsSection() {
  return (
    <section className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <ScrollReveal className="text-center mb-16">
          <p className="text-sm font-semibold text-red-600 dark:text-orange-400 tracking-wider uppercase mb-3">
            El problema
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            ¿Tu equipo también{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500">
              pierde tiempo así?
            </span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Estos problemas le cuestan dinero a tu empresa cada mes.
          </p>
        </ScrollReveal>

        {/* Grid 1→2→3 cols, las 2 últimas en fila de 2 en lg */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {painPoints.map((pain, index) => (
            <PainPointCard key={pain.title} pain={pain} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
